import React, { useState } from 'react';
import styled from '@emotion/styled';
import { RouteComponentProps } from 'react-router';
import { $Values } from 'utility-types';
import * as yup from 'yup';
import { Form, Formik } from 'formik';

import { analytics, auth, SocialAuthType } from '@chronos/firebase';

import useScreenChange from '../../hooks/useScreenChange';
import { Button, TextButton, Logo, Text, ErrorMessage, Collapse, Spacer } from '../../components';
import { useFirebaseUser, useBus } from '../../state';
import { FormInput } from '../../components/Fom';
import GoogleAuth from './GoogleAuth';
import AppleAuth from './AppleAuth';

export const Mode = Object.freeze({
  Login: 'login' as 'login',
  Signup: 'signup' as 'signup',
});
export type Mode = $Values<typeof Mode>;

interface FormValues {
  email: string;
  password: string;
}

const formSchema = yup.object().shape({
  email: yup.string().trim().email('Must be a valid email').required('Required')
    .min(1),
  password: yup.string().trim().required('Required').min(1),
});

const getStrings = (config) => new Map([
  [Mode.Login, {
    title: 'Welcome to Chronos!',
    subtitle: 'Please login to continue.',
    button: 'Login',
    modeSwitchTitle: 'New to Chronos?',
    modeSwitchButton: 'Create Account Here',
    collapseLabel: 'Login with an email',
  }],
  [Mode.Signup, {
    title: 'Create your free account',
    subtitle: 'Free forever. No credit card required.',
    button: 'Signup',
    modeSwitchTitle: 'Already have an account?',
    modeSwitchButton: 'Login Here',
    collapseLabel: 'Sign up with an email',
  }],
]);

export default function Login({ location, history }: RouteComponentProps) {
  const { firebaseUser } = useFirebaseUser();
  const { config } = useBus();

  const mode: Mode = location.pathname === '/login' ? Mode.Login : Mode.Signup;
  const isLoginMode = mode === Mode.Login;
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const urlParams = new URLSearchParams(window.location.search);

  const initialEmail = urlParams.has('email') && (urlParams.get('email') || '');

  useScreenChange(`${mode[0].toUpperCase()}${mode.slice(1)}`);

  if (firebaseUser !== null) {
    // Avoid page flash direct auth redirect
    return null;
  }

  const reset = () => {
    history.replace(`/login${window.location.search}`);
    setIsLoading(false);
  };

  const handleRedirect = () => {
    reset();
    history.replace(`/${window.location.search}`);
  };

  const handleSocialAuth = (type: SocialAuthType) => async () => {
    setIsLoading(true);
    const options = {
      freeTrialDays: config.FREE_TRIAL_DAYS,
    };
    try {
      if (type === SocialAuthType.Apple) {
        await auth.loginWithApple(options);
      } if (type === SocialAuthType.Google) {
        await auth.loginWithGoogle(options);
      }
      history.replace(`/${window.location.search}`);

      if (isLoginMode) {
        handleLoginComplete();
      }
    } catch ({ message }) {
      console.error(message);
      setErrorMessage(`There was an error with ${type} login. Please try again.`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async ({ email, password }: FormValues) => {
    try {
      await auth.loginWithEmailAndPassword(email, password);
      handleRedirect();

      if (isLoginMode) {
        handleLoginComplete();
      }
    } catch ({ message }) {
      console.error(message);

      analytics.logEvent('login - error : login failed', {
        description: message,
      });

      setErrorMessage('There was an error with your login attempt. Please try again.');
    }
  };

  const handleLoginComplete = () => {
    analytics.logEvent('login - success : logged in');
  };

  const handleSignup = async ({ email, password }: FormValues) => {
    try {
      await auth.signupWithEmailAndPassword(email, password, {
        freeTrialDays: config.FREE_TRIAL_DAYS,
        signupLinkPath: location.pathname,
      });

      handleRedirect();
    } catch ({ message }) {
      analytics.logEvent('sign up - error : sign up failed', {
        description: message,
      });

      setErrorMessage(message);
    }
  };

  const handleSubmit = async (values: FormValues) => {
    setIsLoading(true);
    if (isLoginMode) {
      await handleLogin(values);
    } else {
      await handleSignup(values);
    }
    setIsLoading(false);
  };

  const toggleMode = () => {
    history.push(isLoginMode ? `/signup${window.location.search}` : `/login${window.location.search}`);
  };

  const {
    title,
    subtitle,
    button,
    modeSwitchTitle,
    modeSwitchButton,
    collapseLabel,
  } = getStrings(config).get(mode)!;

  return (
    <Formik<FormValues>
      validateOnChange={false}
      validateOnBlur={false}
      validationSchema={formSchema}
      initialValues={{
        email: initialEmail || '',
        password: '',
      }}
      onSubmit={handleSubmit}
    >
      {() => (
        <Form className="login-form">
          <FormContainer>
            <Container>
              <Logo size={100} />
              <Logo text size={40} />

              <Spacer />

              <Title as="h1">{title}</Title>
              <Subtitle>{subtitle}</Subtitle>

              <ErrorMessage>{errorMessage}</ErrorMessage>
            </Container>

            <Collapse label={collapseLabel}>
              <Container>
                <StyledFormInput name="email" label="Email" />
                <StyledFormInput name="password" type="password" label="Password" maxLength={30} />

                <Spacer />

                <ButtonContainer>
                  <Button
                    primary
                    type="submit"
                    loading={isLoading}
                    onClick={() => undefined}
                  >
                    {button}
                  </Button>

                  <Spacer />

                  <TextButton
                    sm
                    hidden={!isLoginMode}
                    text="Forgot password?"
                    disabled={isLoading}
                    onClick={() => history.push('./forgot-password')}
                  />
                </ButtonContainer>
              </Container>
            </Collapse>

            <SocialContainer>
              <GoogleAuth disabled={isLoading} onClick={handleSocialAuth(SocialAuthType.Google)} />

              <Spacer />

              <AppleAuth disabled={isLoading} onClick={handleSocialAuth(SocialAuthType.Apple)} />
            </SocialContainer>

            <Spacer size={20} />
            <Text as="h3">{modeSwitchTitle}</Text>
            <TextButton
              primary
              disabled={isLoading}
              onClick={toggleMode}
              text={modeSwitchButton}
            />

            <Spacer />
            <Text center>
              By signing {isLoginMode ? 'in' : 'up'} you agree to our&nbsp;
              <a
                href="https://chronostimetracking.com/legal/"
                target="_blank"
                rel="noopener noreferrer"
              >
                privacy policy
              </a>.
            </Text>
            <Spacer size={30} />
          </FormContainer>
        </Form>
      )}
    </Formik>
  );
}

const FormContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  maxWidth: 376,
  margin: '0 auto',
  padding: '20px',
});

const Container = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
});

const StyledFormInput = styled(FormInput)({
  width: '100%',
});

const ButtonContainer = styled.div({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
});

const Title = styled(Text)({
  whiteSpace: "nowrap",
});

const Subtitle = styled(Text)({
  marginTop: 10,
  marginBottom: 20,
});

const SocialContainer = styled.div({
  margin: '24px 0',
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
});
